import { useFormatter } from '@api/useFormatter'
import { usePayroll } from '@api/usePayroll'
import { defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { RequestActions } from '@components/DriverManagement/SpecialPayRequests/CellRenderers/RequestActions'
import { RequestPayName } from '@components/DriverManagement/SpecialPayRequests/CellRenderers/RequestPayName'
import { RequestStatusTag } from '@components/DriverManagement/SpecialPayRequests/CellRenderers/RequestStatusTag'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviButton, GraviGrid, Texto } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import { useCallback, useMemo, useRef, useState } from 'react'

import { ExtraFieldsExpand } from './ExtraFieldsExpand'
import { ExtraImageModal } from './ExtraImageModal'
import { Link } from 'react-router-dom'
import { LinkOutlined } from '@ant-design/icons'
import { notification } from 'antd'
import { DateRangeFilter } from '@/components/_SiteWide/DateRangeFilter'
import { gridViewManager } from '@/utils/grid'

const store = window.localStorage
const STORAGE_KEY = 'special-pay-requests'

export function SpecialPayRequests() {
  const gridRef = useRef(null)
  const { scopes, user } = useSecurityContext()
  const { useSpecialPayRequestsQuery } = usePayroll()
  const { updateSetting } = useUserSettings()

  const getDefaultSpecialView = () => {
    const cached = store.getItem('current_special_view')
    if (!cached || !['all', 'open', 'rejected', 'approved'].includes(cached)) {
      return 'all'
    }
    return cached
  }

  // will map to buttons and filters
  const [controlFilters, setControlFilters] = useState({
    status: getDefaultSpecialView(),
  })

  const [dateRangeFilter, setDateRangeFilter] = useState({
    To: moment().endOf('day'),
    From: moment().subtract(31, 'days').startOf('day'),
  })

  const set90DayDateRangeFilter = ({ To, From }) => {
    if (moment(To).diff(moment(From), 'days') > 90) {
      notification.warning({ message: 'Date range cannot exceed 90 days, defaulting to the first 90.' })
      return setDateRangeFilter({
        To: moment(From).add(90, 'days').endOf('day'),
        From,
      })
    }
    return setDateRangeFilter({ To, From })
  }

  const setControlFiltersWrapper = (newFilters) => {
    setControlFilters({ ...newFilters, status: newFilters.status || 'all' })
  }

  const { data: specialPayData, isFetching } = useSpecialPayRequestsQuery(controlFilters, dateRangeFilter)
  const queryClient = useQueryClient()
  // Scopes / Permissions
  const canEditRequests = scopes.driver.special_pay_request?.edit

  const [isImageModalVisible, setIsImageModalVisible] = useState(false)
  const [selectedRow, setSelectedRow] = useState('')

  const openImageModal = (rowId) => {
    const row = specialPayData.rows.find((row) => row.id === rowId)
    setSelectedRow(row)
    setIsImageModalVisible(true)
  }

  const acceptRequest = (id) => {
    api.approveSpecialPay(id).then(() => {
      queryClient.invalidateQueries({ queryKey: ['specialPayRequestsQuery'] })
    })
  }

  const rejectRequest = (id, reason) => {
    api.rejectSpecialPay(id, reason).then(() => {
      queryClient.setQueriesData({ queryKey: ['specialPayRequestsQuery'] }, (oldData) => {
        if (!oldData) return
        return {
          ...oldData,
          rows: oldData.rows.map((row) => {
            if (row.id === id) {
              const newNote = {
                action: 'rejected',
                date: new Date().toISOString(),
                note: reason,
                username: user?.username,
              }
              row.status = 'rejected'
              row.notes = row?.notes?.length ? [...row.notes, newNote] : [newNote]
            }
            return row
          }),
        }
      })

      gridRef?.current?.redrawRows()
    })
  }

  const { formatCurrency } = useFormatter()
  const formattedUnit = formatCurrency(null, true)
  const getRowId = useCallback((row) => row?.data?.id, [])
  const columnDefs = useMemo(
    () => getColumnDefs(rejectRequest, acceptRequest, canEditRequests, openImageModal, formattedUnit),
    [canEditRequests, formattedUnit, openImageModal, rejectRequest, acceptRequest]
  )
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      getRowHeight: (params) => (params?.data?.created_by !== params?.data?.driver_name ? 70 : 50),
      defaultColDef: { filter: true },
    }),
    []
  )

  return (
    <>
      <ExtraImageModal
        isVisible={isImageModalVisible}
        onCancel={() => setIsImageModalVisible(false)}
        fields={selectedRow?.extra_fields}
      />
      <GraviGrid
        externalRef={gridRef}
        controlBarProps={{
          filters: controlFilters,
          setFilters: setControlFiltersWrapper,
          serverParams: specialPayData?.params?.filter((param) => param.filter_column !== 'status'),
          title: 'Special Pay Requests',
          actionButtons: (
            <>
              <StatusFilter controlFilters={controlFilters} setControlFilters={setControlFilters} />
              <DateRangeFilter setCurrentFilters={set90DayDateRangeFilter} currentFilters={dateRangeFilter} />
            </>
          ),
        }}
        loading={isFetching}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        detailCellRenderer={ExtraFieldsExpand}
        masterDetail
        isRowMaster={(data) => data?.extra_fields?.length !== 0}
        storageKey={STORAGE_KEY}
        rowData={specialPayData?.rows}
        detailRowAutoHeight
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </>
  )
}

function StatusFilter({ controlFilters, setControlFilters }) {
  const { updateSetting } = useUserSettings()
  const updateView = (value) => {
    setControlFilters({ status: value })
    store.setItem('current_special_view', value.toLowerCase())
    updateSetting('current_special_view', value.toLowerCase())
  }
  function ViewButton({ value }) {
    return (
      <GraviButton
        onClick={() => updateView(value)}
        style={{ textTransform: 'capitalize' }}
        theme2={controlFilters.status === value.toLowerCase()}
        className='mr-1'
        buttonText={value}
      />
    )
  }

  return (
    <div className='special-pay-status-container'>
      <span className='status mr-3'>STATUS</span>
      <ViewButton value='all' />
      <ViewButton value='open' />
      <ViewButton value='rejected' />
      <ViewButton value='approved' />
    </div>
  )
}

const getColumnDefs = (rejectRequest, acceptRequest, canEditRequests, openImageModal, formattedUnit) => [
  {
    cellRenderer: 'agGroupCellRenderer',
    headerName: 'Extra Fields',
    field: 'id',
    valueFormatter: () => '',
  },
  {
    field: 'status',
    cellRenderer: RequestStatusTag,
    minWidth: 150,
    cellRendererParams: { openImageModal },
  },
  {
    field: 'date',
    filter: 'agDateColumnFilter',
    valueFormatter: (params) => formatters.DATE(moment(params.value), params.data.date_local),
    filterValueGetter: (params) => moment(params.data.date),
    minWidth: 100,
  },
  {
    headerName: 'Driver Name',
    field: 'driver_name',
    cellRenderer: ({ value }) => <Texto weight={'bold'}>{value}</Texto>,
    comparator: (_, __, a, b) =>
      a?.data?.driver_name?.toLowerCase()?.localeCompare(b?.data?.driver_name?.toLowerCase()),
    filter: true,
    minWidth: 200,
  },
  {
    headerName: 'Type',
    filter: true,
    field: 'pay_name',
    cellRenderer: ({ data }) => <RequestPayName data={data} />,
    minWidth: 250,
    comparator: (a, b, nodeA, nodeB) => {
      const aFormat = nodeA.data?.pay_name?.toLowerCase()
      const bFormat = nodeB.data?.pay_name?.toLowerCase()
      const orderBool = aFormat > bFormat
      return orderBool ? 1 : -1
    },
  },

  {
    field: 'rate',
    minWidth: 100,
    filter: 'agNumberColumnFilter',
    valueFormatter: (params) =>
      params.data.unit.includes('percent')
        ? `${(params.value * 100).toFixed(3)}%`
        : `${formattedUnit}${params.value.toFixed(2)}${params?.data.unit ? `/${params?.data.unit}` : ''}`,
  },
  {
    field: 'quantity',
    ...defaultNumberDef('quantity'),
    minWidth: 100,
  },
  {
    field: 'unit',
    minWidth: 100,
  },
  {
    headerName: 'Order Number',
    headerTooltip: 'Update order number in the Payroll Report - Special Pay section',
    field: 'order_number',
    cellRenderer: ({ data }) => {
      if (!data.order_number) return null
      return (
        <Link
          target={'_blank'}
          to={`/order_overview_page/all?${new URLSearchParams({ order_number: data.order_number ?? 1084 })}`}
        >
          <Texto appearance='primary' className='text-truncate'>
            <LinkOutlined className={'mr-2'} /> {data.order_number}
          </Texto>
        </Link>
      )
    },
  },
  {
    field: 'supervisor',
    minWidth: 100,
  },
  {
    headerName: 'Approved By',
    field: 'actions',
    key: 'approved_by',
    minWidth: 100,
    valueGetter: (params) => {
      if (params?.data?.actions?.length > 1 && params?.data?.status === 'approved') {
        return params.data.actions.find((action) => action.status === 'approved')?.username || ''
      }
      return ''
    },
    cellRenderer: (params) => {
      if (params?.data?.actions?.length > 1 && params?.data?.status === 'approved') {
        const approvedBy = params.data.actions.find((action) => action.status === 'approved')?.username
        return approvedBy ? <Texto className='text-truncate'>{approvedBy}</Texto> : null
      }
      return null
    },
  },
  {
    headerName: 'Amount',
    filter: 'agNumberColumnFilter',
    cellRenderer: ({ data }) => (
      <b>
        {formattedUnit}
        {(data.rate * data.quantity).toFixed(2)}
      </b>
    ),
    valueGetter: (params) => params.data.rate * params.data.quantity,
    type: 'rightAligned',
    minWidth: 100,
  },
  {
    headerName: 'Actions',
    cellRenderer: RequestActions,
    cellRendererParams: {
      rejectRequest,
      acceptRequest,
    },
    hide: !canEditRequests,
    filter: false,
    minWidth: 200,
    pinned: 'right',
  },
]
